import { Button, Col, Row } from "antd";
import { useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import Page from "../../Common/Page";
import LoaderContainer from "../../Common/Page/LoaderContainer";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputs } from "../../components/Form/StyledFormInputs";
import SelectCountryCode from "~components/SelectCountryCode/SelectCountryCode.tsx";
import { useFetchSite } from "../../entities/api";
import { useSiteService } from "~pages/Sites/hooks/useSiteService.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { LabelValue } from "~types/labelValue.ts";
import { noLeadingOrTrailingSpacesReactHookFormAdapter } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const PermissionName = PermissionNames.SITE;

interface IFormInput {
  nameLocal: string;
  abbreviationLocal: string;
  nameLatin: string;
  abbreviationLatin: string;
  countryCode: string;
}

const ManageSite = () => {
  const { customerId, dataId, sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const { data, isLoading: isFetchLoading } = useFetchSite();
  const { createSite, isCreatePending, updateSite, isUpdatePending } = useSiteService();

  useEffect(() => {
    reset({
      nameLocal: data?.nameLocal,
      nameLatin: data?.nameLatin,
      abbreviationLocal: data?.abbreviationLocal,
      abbreviationLatin: data?.abbreviationLatin,
      countryCode: data?.countryCode,
    });
  }, [data, reset]);

  const title = useMemo(() => {
    return dataId
      ? `${formatMessage({
          id: "manage",
          defaultMessage: "Manage",
        })} ${data?.nameLocal ? `- ${data?.nameLocal}` : ""}`
      : `${formatMessage({
          id: "addSite",
          defaultMessage: "Adding a New Site",
        })}`;
  }, [dataId, formatMessage, data?.nameLocal]);

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (dataId) {
      await updateSite(dataId, data);
      return;
    }

    await createSite(data);
  };

  return (
    <Page title={title} tabTitle={title}>
      <LoaderContainer loading={isUpdatePending || isCreatePending || isFetchLoading}>
        <Content>
          <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
            <Row style={{ maxWidth: 960 }} gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.local"
                      name="nameLocal"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "site",
                          }),
                        },
                      )}
                      rules={{
                        required: formatMessage(
                          {
                            id: "form.help.required.field",
                          },
                          {
                            field: formatMessage({
                              id: "name.local",
                            }),
                          },
                        ),
                        minLength: {
                          value: 2,
                          message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
                        },
                        validate: {
                          noLeadingOrTrailingSpaces: noLeadingOrTrailingSpacesReactHookFormAdapter(formatMessage),
                        },
                      }}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                      errors={errors}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="abbreviationLocal"
                      labelTooltip={formatMessage({
                        id: "form.help.local.abbreviation",
                      })}
                      name="abbreviationLocal"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
                <FormControl
                  labelText="country"
                  name="countryCode"
                  rules={{
                    required: formatMessage(
                      { id: "form.help.required.field" },
                      {
                        field: formatMessage({
                          id: "country",
                        }),
                      },
                    ),
                  }}
                  control={control}
                  labelTooltip={formatMessage({
                    id: "form.help.league.country",
                  })}
                  render={({ field }) => (
                    <SelectCountryCode
                      value={field.value}
                      onChange={(option) => field.onChange((option as LabelValue | undefined)?.value)}
                    />
                  )}
                  errors={errors}
                />
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.latin"
                      name="nameLatin"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.latin.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "site",
                          }),
                        },
                      )}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="abbreviationLatin"
                      labelTooltip={formatMessage({
                        id: "form.help.latin.abbreviation",
                      })}
                      name="abbreviationLatin"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <PermissionCheck
              permissionKeys={[PermissionName]}
              permissionOptions={dataId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
            >
              <Button htmlType="submit" type="primary">
                {dataId ? formatMessage({ id: "site.update" }) : formatMessage({ id: "site.add" })}
              </Button>
            </PermissionCheck>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/sites`}>
              <Button style={{ marginLeft: 30, marginTop: 10 }} type="primary">
                {formatMessage({ id: "close" })}
              </Button>
            </Link>
          </form>
        </Content>
      </LoaderContainer>
    </Page>
  );
};

const Content = styled.div`
  display: flex;

  .container {
    margin: 0;
  }
`;

export default ManageSite;
